import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { I18nReactContext } from '../../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../../contexts/CourseEditorContext';
import { FormInput } from '../../../../../Form/Input/FormInput';
import { SelectBox } from '../../../../../Pickers/SelectBox/SelectBox';
import { Button } from '../../../../../Form/Button/Button';
import { buttonColors } from '../../../../../Pickers/InputTheme';
import { questionnaireQuestionTypes, questionnaireInputFieldType } from '../../../../../Questionnaires/QuestionnaireConstants';
import CloseIcon from '@mui/icons-material/Close';
import { get } from 'lodash';
import './QuestionSection.scss';

/**
 * QuestionSection - Admin component for creating/editing course questions
 * Takes the whole canvas area like video or background components
 * Updates are saved only when component unmounts
 *
 * @param {Object} props - Component props
 * @param {string} props.sectionId - The section ID containing the question
 */
export const QuestionSection = ({ sectionId }) => {
    const { i18n } = useContext(I18nReactContext);
    const { selectedSection, updateSection } = useContext(CourseEditorContext);

    // Get question data from selected section
    const question = get(selectedSection, 'question', {
        id: uuidv4(),
        text: '',
        type: questionnaireQuestionTypes.MULTIPLE_CHOICE,
        options: [
            { id: uuidv4(), text: '', isCorrect: false },
            { id: uuidv4(), text: '', isCorrect: false }
        ]
    });

    // Local state for question editing
    const [questionText, setQuestionText] = useState(get(question, 'text', ''));
    const [questionType, setQuestionType] = useState(get(question, 'type', questionnaireQuestionTypes.MULTIPLE_CHOICE));
    const [options, setOptions] = useState(get(question, 'options', []));

    // Use ref to track current state for unmount save
    const currentStateRef = useRef({ questionText, questionType, options });

    // Update ref whenever state changes
    useEffect(() => {
        currentStateRef.current = { questionText, questionType, options };
    }, [questionText, questionType, options]);

    // Save changes only on unmount
    useEffect(() => {
        return () => {
            // Cleanup function runs on unmount
            const { questionText, questionType, options } = currentStateRef.current;

            const updatedQuestion = {
                ...question,
                text: questionText,
                type: questionType,
                options: options
            };

            updateSection({
                ...selectedSection,
                question: updatedQuestion
            });
        };
    }, [sectionId]);

    // Handlers
    const handleQuestionTextChange = (e) => {
        setQuestionText(e.target.value);
    };

    const handleQuestionTypeChange = (e) => {
        const newType = e.target.value;
        setQuestionType(newType);

        // When changing from CHECKBOX to MULTIPLE_CHOICE, keep only the first correct answer
        if (newType === questionnaireQuestionTypes.MULTIPLE_CHOICE) {
            setOptions(prevOptions => {
                let foundFirst = false;
                return prevOptions.map(opt => {
                    if (opt.isCorrect && !foundFirst) {
                        foundFirst = true;
                        return opt; // Keep the first correct answer
                    }
                    return { ...opt, isCorrect: false }; // Uncheck all others
                });
            });
        }
    };

    const handleOptionTextChange = useCallback((optionId, newText) => {
        setOptions(prevOptions =>
            prevOptions.map(opt =>
                opt.id === optionId ? { ...opt, text: newText } : opt
            )
        );
    }, []);

    const handleToggleCorrectAnswer = useCallback((optionId) => {
        setOptions(prevOptions => {
            // For MULTIPLE_CHOICE, only one can be correct (radio behavior)
            if (questionType === questionnaireQuestionTypes.MULTIPLE_CHOICE) {
                return prevOptions.map(opt => ({
                    ...opt,
                    isCorrect: opt.id === optionId
                }));
            }
            // For CHECKBOX, multiple can be correct
            else {
                return prevOptions.map(opt =>
                    opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt
                );
            }
        });
    }, [questionType]);

    const handleAddOption = () => {
        setOptions(prevOptions => [
            ...prevOptions,
            { id: uuidv4(), text: '', isCorrect: false }
        ]);
    };

    const handleRemoveOption = useCallback((optionId) => {
        setOptions(prevOptions => {
            // Don't allow removing if only 2 options left
            if (prevOptions.length <= 2) return prevOptions;
            return prevOptions.filter(opt => opt.id !== optionId);
        });
    }, []);

    // Create question type options for SelectBox
    const questionTypeOptions = [
        {
            value: questionnaireQuestionTypes.MULTIPLE_CHOICE,
            text: i18n.translate('questionnaireQuestions.questionOptions.MULTIPLE_CHOICE')
        },
        {
            value: questionnaireQuestionTypes.CHECKBOX,
            text: i18n.translate('questionnaireQuestions.questionOptions.CHECKBOX')
        }
    ];

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <div className="QuestionSection" data-testid="question-section-admin">
            <div className="QuestionSection__container">
                <div className="QuestionSection__header">
                    <FormInput
                        id={`question-text-${sectionId}`}
                        placeholder={i18n.translate('questionnaireQuestions.placeHolders.question')}
                        required
                        dontShowErrorMessage
                        value={questionText}
                        onChange={handleQuestionTextChange}
                        multiline={true}
                        minRows={2}
                        maxRows={4}
                        onFocus={handleFocus}
                        dataTestId="question-text-input"
                        style={{ width: '70%' }}
                    />
                    <div className="QuestionSection__type-selector">
                        <SelectBox
                            options={questionTypeOptions}
                            onChange={handleQuestionTypeChange}
                            value={questionType}
                            dataTestId="question-type-select"
                        />
                    </div>
                </div>

                <div className="QuestionSection__options-list" data-testid="question-options-list">
                    {options.map((option, index) => (
                        <QuestionOptionItem
                            key={option.id}
                            option={option}
                            index={index}
                            questionType={questionType}
                            onTextChange={handleOptionTextChange}
                            onToggleCorrect={handleToggleCorrectAnswer}
                            onRemove={handleRemoveOption}
                            canRemove={options.length > 2}
                        />
                    ))}

                    <Button
                        className="QuestionSection__add-option"
                        color={buttonColors.YELLOW}
                        onClick={handleAddOption}
                        text={String(i18n.translate('questionnaireQuestions.buttons.addOption'))}
                        dataTestId="add-option-button"
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * QuestionOptionItem - Individual option editor component
 */
const QuestionOptionItem = ({ option, index, questionType, onTextChange, onToggleCorrect, onRemove, canRemove }) => {
    const { i18n } = useContext(I18nReactContext);

    const handleTextChange = (e) => {
        onTextChange(option.id, e.target.value);
    };

    const handleToggle = () => {
        onToggleCorrect(option.id);
    };

    const handleRemove = () => {
        onRemove(option.id);
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <div className="QuestionSection__option-item" data-testid={`question-option-${index}`}>
            <input
                type={questionnaireInputFieldType[questionType]}
                checked={option.isCorrect}
                onChange={handleToggle}
                data-testid={`correct-answer-input-${index}`}
            />
            <FormInput
                className="QuestionSection__option-text"
                id={`option-${option.id}`}
                title=""
                label=""
                value={option.text}
                placeholder={`${i18n.translate('questionnaireQuestions.placeHolders.option')} ${index + 1}`}
                onChange={handleTextChange}
                onFocus={handleFocus}
                dataTestId={`option-text-input-${index}`}
            />
            {canRemove && (
                <div
                    className="QuestionSection__remove-option"
                    onClick={handleRemove}
                    data-testid={`delete-option-button-${index}`}
                >
                    <CloseIcon />
                </div>
            )}
        </div>
    );
};
